import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Loader2, FolderKanban } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { FieldError } from "@/components/FieldError";
import { fmtDate } from "@/lib/format";
import { getErrorMessage, getFieldErrors } from "@/lib/errors";

export default function AllProjects() {
  const { isOrgAdmin } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: projectData }, { data: companyData }] = await Promise.all([
        api.get("/projects"), api.get("/masters/companies"),
      ]);
      setProjects(projectData);
      setCompanies(companyData);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "—";

  return (
    <div className="space-y-6" data-testid="all-projects-page">
      <PageHeader
        title="All Projects"
        description="Every project you have access to. Click a row to open its DPR workspace."
        action={isOrgAdmin && <CreateProjectDialog companies={companies} onCreated={load} />}
      />
      <div className="rounded-xl border border-border bg-card">
        {!loading && projects.length === 0 ? (
          <EmptyState title="No projects yet" description="Create your first project, or use Workbook Import to bring in an existing plan." testId="all-projects-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Company</TableHead>
                <TableHead>Contract Value</TableHead><TableHead>Start</TableHead><TableHead>Target End</TableHead><TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {projects.map((p) => (
                <TableRow key={p.id} className="cursor-pointer" onClick={() => navigate(`/projects/${p.id}`)} data-testid={`project-row-${p.code}`}>
                  <TableCell className="font-mono text-xs text-foreground">{p.code}</TableCell>
                  <TableCell className="font-medium text-foreground">{p.name}</TableCell>
                  <TableCell className="text-muted-foreground">{companyName(p.company_id)}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{p.contract_value ? `₹${Number(p.contract_value).toLocaleString("en-IN")}` : "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{fmtDate(p.start_date)}</TableCell>
                  <TableCell className="text-muted-foreground">{fmtDate(p.target_end_date)}</TableCell>
                  <TableCell><StatusPill value={p.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreateProjectDialog({ companies, onCreated }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    code: "", name: "", company_id: "", client_id: "", contract_value: "",
    location: "", start_date: "", target_end_date: "", dc_capacity_mwp: "", wbs_template_id: "__default",
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  useEffect(() => {
    if (open) api.get("/revenue/clients").then(({ data }) => setClients(data)).catch(() => {});
  }, [open]);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/wbs-templates").then(({ data }) => {
      setTemplates(data);
      const preferred = data.find((t) => t.name === "Solar EPC (detailed)") || data[0];
      if (preferred) setForm((f) => (f.wbs_template_id === undefined || f.wbs_template_id === "__default" ? { ...f, wbs_template_id: preferred.id } : f));
    }).catch(() => {});
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    try {
      const payload = {
        ...form,
        company_id: form.company_id || null,
        client_id: form.client_id || null,
        contract_value: form.contract_value || null,
        start_date: form.start_date || null,
        target_end_date: form.target_end_date || null,
        dc_capacity_mwp: form.dc_capacity_mwp ? Number(form.dc_capacity_mwp) : null,
        wbs_template_id: form.wbs_template_id && form.wbs_template_id !== "__default" ? form.wbs_template_id : null,
      };
      const { data } = await api.post("/projects", payload);
      toast.success(payload.wbs_template_id
        ? `Project ${data.code} created with its WBS, weights and DPR rows ready`
        : `Project ${data.code} created — add its WBS via Setup Library or Workbook Import`);
      setOpen(false);
      onCreated?.();
      navigate(`/projects/${data.id}`);
    } catch (err) {
      setErrors(getFieldErrors(err));
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-project-button"><Plus className="h-4 w-4" /> New Project</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FolderKanban className="h-4 w-4" /> New Project</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-project-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Project code</Label><Input placeholder="Automatic if blank" data-testid="project-code-input" value={form.code} onChange={set("code")} className="mt-1.5" /><FieldError error={errors.code} /></div>
            <div><Label>Project name</Label><Input required data-testid="project-name-input" value={form.name} onChange={set("name")} className="mt-1.5" /><FieldError error={errors.name} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Company</Label>
              <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger data-testid="project-company-select" className="mt-1.5"><SelectValue placeholder="Select company" /></SelectTrigger>
                <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Client</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="project-client-select" className="mt-1.5"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Contract value (₹)</Label><Input type="number" step="0.01" data-testid="project-contract-value-input" value={form.contract_value} onChange={set("contract_value")} className="mt-1.5" /></div>
            <div><Label>Location</Label><Input data-testid="project-location-input" value={form.location} onChange={set("location")} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>DC capacity (MWp)</Label><Input type="number" step="0.01" min="0" data-testid="project-dc-capacity-input" value={form.dc_capacity_mwp} onChange={set("dc_capacity_mwp")} className="mt-1.5" /></div>
            <div />
          </div>
          <div>
            <Label>WBS template <span className="font-normal text-muted-foreground">(builds Engineering, Procurement &amp; Construction automatically)</span></Label>
            <Select value={form.wbs_template_id || "none"} onValueChange={(v) => setForm({ ...form, wbs_template_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="project-template-select" className="mt-1.5"><SelectValue placeholder="Select template" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No template — I'll build the WBS myself</SelectItem>
                {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {form.wbs_template_id && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                {form.dc_capacity_mwp ? `Quantities per MWp will be scaled for ${form.dc_capacity_mwp} MWp.` : "Enter the DC capacity so quantities per MWp can be calculated."}
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Start date</Label><Input type="date" data-testid="project-start-date-input" value={form.start_date} onChange={set("start_date")} className="mt-1.5" /></div>
            <div><Label>Target end date</Label><Input type="date" data-testid="project-end-date-input" value={form.target_end_date} onChange={set("target_end_date")} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-project-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create Project</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
